import {
    Table as DefaultTable,
    TableBody as DefaultTableBody,
    TableCell as DefaultTableCell,
    TableCellEmpty as DefaultTableCellEmpty,
    TableCellLoading as DefaultTableCellLoading,
    TableHead as DefaultTableHead,
    TableHeader as DefaultTableHeader,
    TableRow as DefaultTableRow,
} from '@/components/ui/table';
import type { Column, ColumnDef, Table as ReactTable, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import { forwardRef } from 'react';
import { useInView } from 'react-intersection-observer';

export type TableComponents = {
    Table?: React.ElementType;
    TableBody?: React.ElementType;
    TableCell?: React.ElementType;
    TableCellEmpty?: React.ElementType;
    TableCellLoading?: React.ElementType;
    TableHead?: React.ElementType;
    TableHeader?: React.ElementType;
    TableRow?: React.ElementType;
};

// Make hasNextPage required if isFetchingNextPage is provided

type InfiniteScrollProps = {
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onFetchNextPage: () => void;
};

type BaseDataTableProps = {
    isLoading: boolean;
    totalRowsCount?: number;
    table: ReactTable<any>;
    columns?: ColumnDef<any>[];
    className?: string;
    onRowClick?: (row: Row<any>) => void;
    components?: TableComponents;
    disableHeader?: boolean;
    summaryHeader?: Record<string, any>;
} & React.HTMLAttributes<HTMLTableElement>;

type Props = BaseDataTableProps & (InfiniteScrollProps | Partial<InfiniteScrollProps>);

type Meta = {
    align?: 'left' | 'right';
};

const DataTable = forwardRef<HTMLTableElement, Props>(
    (
        {
            disableHeader = false,
            isLoading,
            onScroll,
            totalRowsCount = Number.POSITIVE_INFINITY,
            onRowClick,
            table,
            className,
            isFetchingNextPage,
            onFetchNextPage,
            hasNextPage,
            components = {},
            summaryHeader,
        },
        ref,
    ) => {
        // Destructure with defaults
        const {
            Table = DefaultTable,
            TableBody = DefaultTableBody,
            TableCell = DefaultTableCell,
            TableCellEmpty = DefaultTableCellEmpty,
            TableCellLoading = DefaultTableCellLoading,
            TableHead = DefaultTableHead,
            TableHeader = DefaultTableHeader,
            TableRow = DefaultTableRow,
        } = components;

        const columnsCount = table.getAllColumns().length || 0;
        const rowCount = table.getRowModel().rows.length || 0;
        const isEmptyTable = rowCount === 0;

        const { ref: lastRowToFetchRef } = useInView({
            threshold: 0.1,
            onChange: (inView) => {
                if (inView && onFetchNextPage && !isFetchingNextPage && hasNextPage) {
                    onFetchNextPage();
                }
            },
            skip: isEmptyTable,
        });

        return (
            <Table
                ref={ref}
                className={className}
                onScroll={onScroll}
                style={{ minWidth: isEmptyTable ? undefined : table.getCenterTotalSize() }}
            >
                {!disableHeader && (
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            style={getCommonPinningStyles(header.column)}
                                            align={
                                                (header.column.columnDef.meta as Meta)?.align === 'right'
                                                    ? 'right'
                                                    : 'left'
                                            }
                                        >
                                            {header.isPlaceholder || isLoading || isEmptyTable
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef.header,
                                                      header.getContext(),
                                                  )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}

                        {/* Summary Header Row */}
                        {summaryHeader && (
                            <TableRow>
                                {table.getAllColumns().map((column) => {
                                    const columnDef = column.columnDef as ColumnDef<any, any> & {
                                        accessorKey?: string;
                                        summary?: (data: unknown) => React.ReactNode;
                                    };
                                    const columnKey = columnDef.accessorKey ?? column.id;
                                    // Use column summary function if available, otherwise use value from summaryHeader
                                    const value =
                                        typeof columnDef.summary === 'function'
                                            ? columnDef.summary(summaryHeader)
                                            : summaryHeader[columnKey];
                                    const align =
                                        (columnDef.meta as Meta)?.align === 'right' ? 'right' : 'left';

                                    return (
                                        <TableHead
                                            key={column.id}
                                            align={align}
                                            style={getCommonPinningStyles(column)}
                                            className="bg-white font-bold text-neutral-950"
                                        >
                                            {value}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        )}
                    </TableHeader>
                )}

                <TableBody>
                    {!isLoading && !isEmptyTable
                        ? table.getRowModel().rows.map((row, idx, arr) => (
                              <TableRow
                                  key={row.id}
                                  ref={
                                      // This can improve UX by fetching in the future
                                      idx === arr.length - (totalRowsCount === arr.length ? 1 : 10)
                                          ? lastRowToFetchRef
                                          : undefined
                                  }
                                  data-state={row.getIsSelected() && 'selected'}
                                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                              >
                                  {row.getAllCells().map((cell) => (
                                      <TableCell
                                          key={cell.id}
                                          data-state={row.getIsSelected() && 'selected'}
                                          className={
                                              cell.column.getIsPinned()
                                                  ? 'bg-white group-hover:bg-neutral-25'
                                                  : ''
                                          }
                                          style={getCommonPinningStyles(cell.column)}
                                          align={
                                              (cell.column.columnDef.meta as Meta)?.align === 'right'
                                                  ? 'right'
                                                  : 'left'
                                          }
                                      >
                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                      </TableCell>
                                  ))}
                              </TableRow>
                          ))
                        : null}

                    {isLoading || isFetchingNextPage ? (
                        <TableCellLoading
                            colSpan={columnsCount}
                            className={isFetchingNextPage ? 'h-32' : ''}
                        />
                    ) : null}

                    {!isLoading && isEmptyTable ? <TableCellEmpty colSpan={columnsCount} /> : null}
                </TableBody>
            </Table>
        );
    },
);

function getCommonPinningStyles(column: Column<unknown>): CSSProperties {
    const isPinned = column.getIsPinned();

    return {
        left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
        right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
        opacity: isPinned ? 0.95 : 1,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
    };
}

DataTable.displayName = 'DataTable';

export { DataTable };
